import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, Shield, Eye, EyeOff } from "lucide-react";
import SmartLogo from "../components/SmartLogo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const formatErr = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

const GoogleLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.1z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 2FA state — only used for admin account
  const [requires2FA, setRequires2FA] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [verifying2FA, setVerifying2FA] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      // 202 means admin 2FA required
      if (res.status === 202 && res.data?.requires_2fa) {
        setRequires2FA(true);
        toast.info("2FA code sent to admin email. Please check your inbox.");
        return;
      }
      // Normal login success — store token for mobile cookie fallback
      if (res.data.session_token) setToken(res.data.session_token);
      toast.success("Welcome back");
      navigate("/dashboard", { state: { user: res.data.user } });
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || "Login failed";
      setErrorMsg(msg);
    } finally { setBusy(false); }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) { setErrorMsg("Enter the 6-digit code from your email."); return; }
    setErrorMsg("");
    setVerifying2FA(true);
    try {
      const res = await api.post("/auth/login/verify-2fa", {
        email, password, otp: otpCode, remember_me: rememberMe,
      });
      if (res.data.session_token) setToken(res.data.session_token);
      toast.success("Welcome back, Admin");
      navigate("/dashboard", { state: { user: res.data.user } });
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || "Verification failed";
      setErrorMsg(msg);
    } finally { setVerifying2FA(false); }
  };

  const resend2FA = async () => {
    try {
      await api.post("/auth/login", { email, password, remember_me: rememberMe });
      toast.info("A new 2FA code has been sent to your email.");
    } catch {
      toast.error("Could not resend code. Please try again.");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]" data-testid="login-page">
      {/* Left – form */}
      <div className="flex flex-col px-6 sm:px-12 py-10 lg:py-16">
        <div className="mb-12">
          <SmartLogo />
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 flex-1 flex flex-col justify-center">
          <span className="label-eyebrow">Welcome back</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
            Sign in to your dashboard.
          </h1>

          {/* ── Normal login form ── */}
          {!requires2FA && (
            <form onSubmit={handleEmailLogin} className="mt-8 space-y-4" data-testid="login-email-form">
              <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" testId="login-email" required />
              <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" testId="login-password" required />

              {errorMsg && (
                <div className="px-3 py-2 border border-red-200 bg-red-50 text-red-800 text-sm rounded-sm" data-testid="login-error">
                  {errorMsg}
                </div>
              )}

              {/* Remember me + Forgot password row */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    data-testid="login-remember-me"
                    className="w-4 h-4 rounded border-zinc-300 text-zinc-950 accent-zinc-950 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-600">Remember this device</span>
                </label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-zinc-600 hover:text-zinc-950 underline">
                  Forgot password?
                </Link>
              </div>

              {/* Sign in row: email button + Google button side-by-side */}
              <div className="flex gap-2">
                <button type="submit" disabled={busy} data-testid="login-submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-4 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                  {busy ? "Signing in…" : "Sign in"} <ArrowRight size={15} />
                </button>
                <button type="button" onClick={handleGoogle} data-testid="login-google-button"
                  title="Continue with Google"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-zinc-200 text-zinc-950 font-semibold px-4 py-3 rounded-sm hover:bg-zinc-50 transition-colors">
                  <GoogleLogo />
                  <span className="hidden sm:inline">Google</span>
                </button>
              </div>

              <p className="text-sm text-zinc-600 text-center pt-2">
                New here?{" "}
                <Link to="/signup" data-testid="signup-link" className="font-semibold text-zinc-950 underline">
                  Create an account
                </Link>
              </p>
            </form>
          )}

          {/* ── Admin 2FA form ── */}
          {requires2FA && (
            <form onSubmit={handle2FAVerify} className="mt-8 space-y-4" data-testid="admin-2fa-form">
              <div className="flex items-center gap-3 p-4 border border-amber-200 bg-amber-50 rounded-sm">
                <Shield size={18} className="text-amber-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-zinc-900">Two-factor authentication required</p>
                  <p className="text-xs text-zinc-600 mt-0.5">A 6-digit code was sent to the admin email. Enter it below to continue.</p>
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-1.5">2FA Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="6-digit code"
                  data-testid="admin-2fa-input"
                  className="w-full px-3 py-2.5 border border-zinc-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1 text-center font-mono text-xl tracking-[0.5em]"
                  autoFocus
                />
              </div>

              {errorMsg && (
                <div className="px-3 py-2 border border-red-200 bg-red-50 text-red-800 text-sm rounded-sm">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={verifying2FA || otpCode.length !== 6}
                data-testid="admin-2fa-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-4 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50 transition-colors"
              >
                {verifying2FA ? "Verifying…" : "Verify & Sign in"} <ArrowRight size={15} />
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resend2FA}
                  className="text-xs text-zinc-500 hover:text-zinc-800 underline"
                >
                  Resend code
                </button>
                <span className="text-zinc-300 mx-2">|</span>
                <button
                  type="button"
                  onClick={() => { setRequires2FA(false); setOtpCode(""); setErrorMsg(""); }}
                  className="text-xs text-zinc-500 hover:text-zinc-800 underline"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}

          <p className="mt-10 text-xs text-zinc-500">
            By continuing, you agree to the Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>

      {/* Right – image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1552505557-c085030eea99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="NYC construction skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <span className="label-eyebrow text-amber-400">// Built for the field</span>
          <p className="font-display text-2xl font-bold tracking-tight mt-2 max-w-md">
            "We used to lose two hours a day refreshing DOB NOW. Now we lose zero."
          </p>
          <p className="text-sm text-zinc-300 mt-2">— Project Manager, Brooklyn-based GC</p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, type, value, onChange, placeholder, testId, required }) => {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (showPw ? "text" : "password") : type;
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />}
        <input
          type={inputType} required={required} value={value}
          onChange={e => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testId}
          className={`w-full ${Icon ? "pl-9" : "pl-3"} ${isPassword ? "pr-9" : "pr-3"} py-2.5 border border-zinc-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
            tabIndex={-1}
            aria-label={showPw ? "Hide password" : "Show password"}
          >
            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
};
